import React, { useEffect, useRef } from "react";
import {
  Cloud,
  Sun,
  CloudRain,
  Wind,
  Droplets,
  Eye,
  Gauge,
  RefreshCw,
  MapPin,
  Star,
  Search,
  Settings,
  AlertCircle,
} from "lucide-react";
import { useWeather } from "@/hooks/use-weather";
import { clearWeatherCache, getWeatherDebugInfo } from "@/utils/weather-debug";

interface WeatherWidgetProps {
  className?: string;
}

const SimpleWeatherWidget: React.FC<WeatherWidgetProps> = ({
  className = "",
}) => {
  const {
    weather,
    isLoading,
    error,
    getCurrentLocationWeather,
    clearError,
    hasWeatherData,
  } = useWeather();

  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!hasWeatherData && !hasFetchedRef.current && !isLoading) {
      // Clear any existing timeout
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      // Debounce the fetch call
      fetchTimeoutRef.current = setTimeout(() => {
        hasFetchedRef.current = true;
        getCurrentLocationWeather().catch((err) => {
          hasFetchedRef.current = false; // Reset on error to allow retry
        });
      }, 500); // 500ms debounce
    }

    // Cleanup timeout on unmount
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [hasWeatherData, getCurrentLocationWeather, isLoading]);

  const handleClearCache = () => {
    clearWeatherCache();
    window.location.reload();
  };

  const handleDebugInfo = () => {
    getWeatherDebugInfo();
  };

  if (error) {
    return (
      <div
        className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}
      >
        <div className="flex items-center justify-between">
          <div className="text-red-600 text-sm">{error}</div>
          <button
            onClick={clearError}
            className="text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
        <button
          onClick={getCurrentLocationWeather}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (isLoading && !hasWeatherData && !weather) {
    return (
      <div className={` ${className}`}>
        <div className="animate-pulse">
          <div className="flex items-center space-x-3">
            <div className="w-18 h-8 bg-gray-300/50 rounded-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!weather) {
    return (
      <div
        className={`bg-gray-50 border border-gray-200 rounded-lg flex gap-2 text-xs ${className}`}
      >
        <button
          onClick={getCurrentLocationWeather}
          className="text-blue-600 hover:text-blue-800 underline"
        >
          reload weather
        </button>
      </div>
    );
  }

  return (
    <div className={`flex items-center ${className}`}>
      <div className="text-2xl">{weather.current.condition.icon}</div>
      <div>
        <div className="text-sm md:text-lg text-white">
          <b> {weather.formatted.temperatureValue}</b>{" "}
          <sup>{weather.formatted.temperatureUnit}</sup>
        </div>
      </div>
    </div>
  );
};

export default SimpleWeatherWidget;
